/**
 * HitEffects — Pre-built composable on-hit effect factories.
 *
 * Each factory returns a HitEffect function that can be attached to any
 * ability via .withOnHit(), .withOnCrit(), or .withOnKill().
 *
 * L3.4 — DOTs now route through the server. Each effect:
 *   1. Sends an `applyStatusEffect` message — server is authoritative
 *      for tick damage / state / cross-viewer broadcast.
 *   2. Emits the local VFX immediately (prediction). Server's
 *      `statusApplied` broadcast confirms; spectators get visuals
 *      through the same broadcast via the StatusEffect bridge.
 *
 * Pre-L3.4 the local statusEffectManager.apply() also self-ticked,
 * but only for FCT — actual HP never moved. The new flow drops the
 * self-tick (server is sole authority) and keeps the apply() call
 * just to register the local stack-count for the effect-icon UI.
 */

import { EventBus } from '../../../../../shared/EventBus';
import { statusEffectManager } from '../../../../systems/combat/StatusEffectManager';
import type { HitEffect } from './types';

const eventBus = EventBus.getInstance();

/** L3.4 — best-effort send of the server-authoritative status apply.
 *  Falls through silently when room/network isn't available (offline
 *  ability rigs, demo modes). The local-VFX path runs regardless. */
function sendServerStatus(
  targetId: string,
  effectId: string,
  spec: { durationMs: number; tickRateMs?: number; tickDamage?: number; tickHeal?: number; data?: Record<string, any> },
): void {
  try {
    const room = (globalThis as any).nm?.getRoom?.();
    if (!room?.send) return;
    room.send('applyStatusEffect', { targetId, effectId, ...spec });
  } catch {
    // Network not ready — local prediction still ran; server-side
    // damage just won't apply for this cast. Acceptable in offline /
    // dev-rig contexts.
  }
}

/** Apply a poison DoT to the target */
export function applyPoison(config: { damage: number; duration: number; ticks?: number; maxStacks?: number }): HitEffect {
  return (ctx) => {
    const tickRateMs = config.duration / (config.ticks ?? 3);
    // L3.4 — server is sole authority for tick damage.
    sendServerStatus(ctx.targetId, 'poison', {
      durationMs: config.duration,
      tickRateMs,
      tickDamage: config.damage,
    });
    statusEffectManager.apply({
      effectId: 'poison',
      targetId: ctx.targetId,
      sourceId: ctx.attackerId,
      duration: config.duration,
      tickDamage: config.damage,
      tickRateMs,
      maxStacks: config.maxStacks ?? 5,
    });
    eventBus.emit('vfx:spawn', { effectId: 'preset_poison_cloud', x: ctx.position.x, y: ctx.position.y });
  };
}

/** Apply a fire DoT (burn) to the target */
export function applyBurn(damage: number, duration: number, maxStacks?: number): HitEffect {
  return (ctx) => {
    sendServerStatus(ctx.targetId, 'burn', {
      durationMs: duration,
      tickRateMs: 1000,
      tickDamage: damage,
    });
    statusEffectManager.apply({
      effectId: 'burn',
      targetId: ctx.targetId,
      sourceId: ctx.attackerId,
      duration,
      tickDamage: damage,
      tickRateMs: 1000,
      maxStacks: maxStacks ?? 3,
    });
    eventBus.emit('vfx:spawn', { effectId: 'preset_fire_trail', x: ctx.position.x, y: ctx.position.y });
  };
}

/** Apply a movement slow to the target */
export function applySlow(percent: number, duration: number): HitEffect {
  return (ctx) => {
    // L3.4 — slow ticks no damage but server still owns the state flag
    // so future input-gates / cross-viewer presence can read it.
    sendServerStatus(ctx.targetId, 'slow', {
      durationMs: duration,
      data: { speedMultiplier: -percent },
    });
    statusEffectManager.apply({
      effectId: 'slow',
      targetId: ctx.targetId,
      sourceId: ctx.attackerId,
      duration,
      maxStacks: 1,
    });
    eventBus.emit('combat:apply_effect', {
      targetId: ctx.targetId,
      casterId: ctx.attackerId,
      effect: { type: 'debuff', stat: 'speedMultiplier', value: -percent, duration },
    });
    eventBus.emit('vfx:spawn', { effectId: 'preset_frost_mist', x: ctx.position.x, y: ctx.position.y });
  };
}

/** Apply a freeze/stun to the target */
export function applyFreeze(duration: number): HitEffect {
  return (ctx) => {
    sendServerStatus(ctx.targetId, 'frozen', {
      durationMs: duration,
    });
    statusEffectManager.apply({
      effectId: 'frozen',
      targetId: ctx.targetId,
      sourceId: ctx.attackerId,
      duration,
      maxStacks: 1,
    });
    eventBus.emit('combat:apply_effect', {
      targetId: ctx.targetId,
      casterId: ctx.attackerId,
      effect: { type: 'debuff', stat: 'frozen', value: 1, duration },
    });
    eventBus.emit('vfx:spawn', { effectId: 'preset_frost_mist', x: ctx.position.x, y: ctx.position.y });
  };
}

/** Heal the caster for a % of damage dealt */
export function lifesteal(percent: number): HitEffect {
  return (ctx) => {
    const healAmount = Math.floor(ctx.damage * percent);
    if (healAmount <= 0) return;
    eventBus.emit('vfx:damage_number', {
      x: ctx.position.x,
      y: ctx.position.y - 20,
      amount: healAmount,
      type: 'heal',
    });
    eventBus.emit('combat:lifesteal', {
      attackerId: ctx.attackerId,
      amount: healAmount,
    });
  };
}

/** Push the target away from the attacker */
export function knockback(distance: number): HitEffect {
  return (ctx) => {
    eventBus.emit('combat:knockback', {
      targetId: ctx.targetId,
      attackerId: ctx.attackerId,
      distance,
      x: ctx.position.x,
      y: ctx.position.y,
    });
  };
}

/** Deal bonus damage as a % of the original hit */
export function bonusDamage(percent: number): HitEffect {
  return (ctx) => {
    const bonus = Math.floor(ctx.damage * percent);
    if (bonus <= 0) return;
    eventBus.emit('combat:bonus_damage', {
      targetId: ctx.targetId,
      attackerId: ctx.attackerId,
      damage: bonus,
    });
    eventBus.emit('vfx:damage_number', {
      x: ctx.position.x + 10,
      y: ctx.position.y - 10,
      amount: bonus,
      type: 'damage',
    });
  };
}

/** Heal the caster for a flat amount */
export function healSelf(amount: number): HitEffect {
  return (ctx) => {
    eventBus.emit('combat:lifesteal', {
      attackerId: ctx.attackerId,
      amount,
    });
    eventBus.emit('vfx:damage_number', {
      x: ctx.position.x,
      y: ctx.position.y - 20,
      amount,
      type: 'heal',
    });
  };
}

/** Restore caster mana */
export function manaRestore(amount: number): HitEffect {
  return (ctx) => {
    eventBus.emit('combat:mana_restore', {
      playerId: ctx.attackerId,
      amount,
    });
  };
}
